import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { XR } from "@react-three/xr";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { USMap } from "./USMap";
import { InstitutionNode } from "./InstitutionNode";
import { StateCluster } from "./StateCluster";
import { nodeHeight, footprintRadius } from "../lib/scoring";
import { computeLayout } from "../lib/layout";
import { project } from "../lib/projection";

// Smoothly flies the camera + orbit target toward a requested view.
function CameraRig({ request }) {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls);
  const desiredPos = useRef(null);
  const desiredTarget = useRef(null);

  useEffect(() => {
    if (request) {
      desiredPos.current = new THREE.Vector3(...request.cam);
      desiredTarget.current = new THREE.Vector3(...request.target);
    }
  }, [request]);

  useFrame(() => {
    if (!desiredPos.current || !controls) return;
    camera.position.lerp(desiredPos.current, 0.06);
    controls.target.lerp(desiredTarget.current, 0.06);
    controls.update();
    if (camera.position.distanceTo(desiredPos.current) < 0.25) {
      desiredPos.current = null;
    }
  });

  return null;
}

// Reports whether the camera is zoomed in past a threshold (to reveal node seals).
function ZoomWatcher({ onChange }) {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls);
  const last = useRef(null);
  useFrame(() => {
    if (!controls) return;
    const d = camera.position.distanceTo(controls.target);
    const zi = d < 15;
    if (last.current !== zi) {
      last.current = zi;
      onChange(zi);
    }
  });
  return null;
}

// A quick expanding ground ring "flash" sized to the school's real-estate footprint.
function FootprintReveal({ target }) {
  const ref = useRef();
  const start = useRef(0);
  const active = useRef(false);

  useEffect(() => {
    if (target) {
      start.current = performance.now();
      active.current = true;
    }
  }, [target]);

  useFrame(() => {
    if (!ref.current) return;
    if (!active.current) {
      ref.current.visible = false;
      return;
    }
    const t = (performance.now() - start.current) / 950;
    if (t >= 1) {
      active.current = false;
      ref.current.visible = false;
      return;
    }
    ref.current.visible = true;
    const s = Math.max(0.001, target.radius * t);
    ref.current.scale.set(s, 1, s);
  });

  if (!target) return null;
  return (
    <mesh
      ref={ref}
      position={[target.x, 0.07, target.z]}
      rotation={[-Math.PI / 2, 0, 0]}
      visible={false}
    >
      <ringGeometry args={[0.9, 1, 44]} />
      <meshStandardMaterial
        color={target.color}
        emissive={target.color}
        emissiveIntensity={0.55}
        metalness={0.3}
        roughness={0.4}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

export function Scene({
  institutions,
  activeRank,
  focusRank,
  revealNonce,
  region,
  onHover,
  onSelect,
  onZoomChange,
  onClusterHover,
  xrStore,
  category,
  ranges,
  compareList,
  bloom,
  visibleRanks,
  highlight,
}) {
  const [zoomedIn, setZoomedIn] = useState(false);
  const [flyRequest, setFlyRequest] = useState(null);

  const layout = useMemo(() => computeLayout(institutions), [institutions]);
  const byRank = useMemo(() => {
    const m = {};
    institutions.forEach((i) => (m[i.rank] = i));
    return m;
  }, [institutions]);

  // Group the currently-visible institutions into per-state clusters. Each
  // cluster sits at the centroid of its schools' true map coordinates and is
  // coloured by its top-ranked (best) institution.
  const clusters = useMemo(() => {
    const groups = {};
    institutions.forEach((inst) => {
      if (visibleRanks && !visibleRanks.has(inst.rank)) return;
      const n = layout[inst.rank];
      if (!n) return;
      const g = groups[inst.state] || (groups[inst.state] = { sx: 0, sz: 0, items: [] });
      g.sx += n.ox;
      g.sz += n.oz;
      g.items.push(inst);
    });
    return Object.entries(groups).map(([state, g]) => {
      const items = g.items.slice().sort((a, b) => a.rank - b.rank);
      return {
        state,
        count: items.length,
        x: g.sx / items.length,
        z: g.sz / items.length,
        color: items[0].color || "#B8912F",
        schools: items,
      };
    });
  }, [institutions, visibleRanks, layout]);

  // Leader lines from displaced (visible) nodes back to true geographic coordinates.
  const leaderGeometry = useMemo(() => {
    const pos = [];
    Object.values(layout).forEach((n) => {
      if (visibleRanks && !visibleRanks.has(n.rank)) return;
      const dd = Math.hypot(n.dx - n.ox, n.dz - n.oz);
      if (dd > 0.25) {
        pos.push(n.dx, 0.05, n.dz, n.ox, 0.05, n.oz);
      }
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    return g;
  }, [layout, visibleRanks]);

  // Fly to a selected institution.
  useEffect(() => {
    if (focusRank == null) return;
    const n = layout[focusRank];
    const inst = byRank[focusRank];
    if (!n || !inst) return;
    const h = nodeHeight(inst);
    setFlyRequest({
      target: [n.dx, h * 0.5, n.dz],
      cam: [n.dx + 5, h * 0.6 + 6, n.dz + 9],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRank]);

  // Fly to a geographic region cluster.
  useEffect(() => {
    if (!region) return;
    const [x, z] = project(region.center[0], region.center[1]);
    setFlyRequest({
      target: [x, 2, z],
      cam: [x, region.dist * 0.72, z + region.dist * 0.9],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region?.nonce]);

  const revealTarget = useMemo(() => {
    if (focusRank == null) return null;
    const n = layout[focusRank];
    const inst = byRank[focusRank];
    if (!n || !inst) return null;
    return {
      x: n.dx,
      z: n.dz,
      radius: footprintRadius(inst),
      color: inst.color || "#B8912F",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealNonce, focusRank]);

  const handleZoom = (zi) => {
    setZoomedIn(zi);
    if (onZoomChange) onZoomChange(zi);
  };

  // Clicking a state bubble flies in close enough to reveal individual towers.
  const handleClusterSelect = (c) => {
    setFlyRequest({
      target: [c.x, 2, c.z],
      cam: [c.x, 7.5, c.z + 9],
    });
  };

  return (
    <Canvas
      shadows
      dpr={[1, 2]}
      camera={{ position: [2, 12, 17], fov: 45 }}
      gl={{ antialias: true, alpha: false, preserveDrawingBuffer: true }}
    >
      <color attach="background" args={["#FCFCFD"]} />
      <fog attach="fog" args={["#FCFCFD", 34, 62]} />

      <XR store={xrStore}>
        <Suspense fallback={null}>
          <ambientLight intensity={0.95} />
          <hemisphereLight args={["#ffffff", "#e7e9ee", 0.7]} />
          <directionalLight
            position={[10, 18, 8]}
            intensity={1.1}
            castShadow
            shadow-mapSize={[2048, 2048]}
            shadow-camera-left={-24}
            shadow-camera-right={24}
            shadow-camera-top={24}
            shadow-camera-bottom={-24}
          />

          <USMap />

          {/* Zoomed OUT → state clusters; zoomed IN → individual towers */}
          {!zoomedIn &&
            clusters.map((c) => (
              <StateCluster
                key={c.state}
                cluster={c}
                onSelect={handleClusterSelect}
                onHover={onClusterHover}
              />
            ))}

          {zoomedIn && (
            <>
              {/* Leader lines back to true coordinates */}
              <lineSegments geometry={leaderGeometry}>
                <lineBasicMaterial color="#AAB0BA" transparent opacity={0.55} />
              </lineSegments>

              <FootprintReveal target={revealTarget} />

              {institutions.map((inst) => {
                const n = layout[inst.rank];
                const catKey = category === "composite" ? null : category;
                return (
                  <InstitutionNode
                    key={inst.rank}
                    institution={inst}
                    pos={[n.dx, n.dz]}
                    isActive={activeRank === inst.rank}
                    onHover={onHover}
                    onSelect={onSelect}
                    category={category}
                    range={catKey ? ranges?.[catKey] : null}
                    compareIndex={compareList.indexOf(inst.rank)}
                    bloom={bloom}
                    showSeal={zoomedIn}
                    visible={!visibleRanks || visibleRanks.has(inst.rank)}
                    pulseKey={
                      highlight && highlight.rank === inst.rank ? highlight.nonce : 0
                    }
                  />
                );
              })}
            </>
          )}
        </Suspense>
      </XR>

      <OrbitControls
        makeDefault
        enablePan
        enableDamping
        dampingFactor={0.08}
        minDistance={5}
        maxDistance={34}
        maxPolarAngle={Math.PI / 2 - 0.04}
        target={[0, 1.5, 0]}
      />
      <CameraRig request={flyRequest} />
      <ZoomWatcher onChange={handleZoom} />
    </Canvas>
  );
}
